import { ExternalLinkIcon, LinkIcon, AddIcon } from "@chakra-ui/icons";
import {
  Alert,
  Box,
  Button,
  Container,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  SimpleGrid,
  Spacer,
  Spinner,
  Stack,
  Tab,
  Table,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Text,
  Tfoot,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useMediaQuery,
} from "@chakra-ui/react";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState } from "react";
import { AlertCircle, Cloud, CloudOff, Edit3, Upload } from "react-feather";
import { useLocation } from "react-router-dom";
import {
  fetchCustomerAccounts,
  fetchAccountTypes,
  fetchAccountSubTypes,
  fetchAccountTaxTypes,
  createAccount,
} from "../../api/accounts";
import { fetchCustomer } from "../../api/customers";
import {
  addSubscription,
  cancelSubscription,
  fetchPlaiditems,
  fetchSubscription,
  fetchSubscriptionSettings,
  updateBankAccount,
  createBankAccount,
} from "../../api/plaid";
import { Context, useCurrentProfile } from "../../ContextWrapper";
import CustomTableContainer from "../../theme/components/tableContainer";
import {
  AccessToken,
  isFeatureEnabled,
  renderAmount,
  titleCase,
  parseFilePreview,
} from "../../utils";
import { DropDown } from "../generic/dropDown";
import NotificationContainer from "../generic/updateNotification";
import { LinkPlaidButton } from "../plaid/linkPlaid";
import { PlaidBankTransactionExport } from "../plaid/plaidBankTransaction";
import { PlaidItemCreate } from "../plaid/plaidItemCreate";
import ClientPLaidItemDelete from "./clientPlaidDelete";
import { GenericModal } from "../generic/modal";
import EditQuickbooksName from "./editQuickbooksName";
import { createCheckoutSession } from "../../api/stripe";
import {
  CheckoutProvider,
  useCheckout,
  PaymentElement,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { AccountModal } from "../account/chartOfAccounts";
import { importTransactions } from "../../api/imports";

export const ClientPlaid = ({ customer_id, setNavVisible = null }) => {
  const [plaidItemList, setPlaidItemList] = useState([]);
  const [accountList, setAccountList] = useState([]);
  // Raw accounts for AccountModal parent dropdown
  const [accountListRaw, setAccountListRaw] = useState([]);
  // Account type metadata for AccountModal
  const [accountTypeList, setAccountTypeList] = useState([]);
  const [accountSubTypeList, setAccountSubTypeList] = useState([]);
  const [accountTaxTypeList, setAccountTaxTypeList] = useState([]);
  // Quick create modal state
  const [isAccountModalOpen, setIsAccountModalOpen] = useState(false);
  const [accountCreating, setAccountCreating] = useState(false);
  const [bankAccountToLink, setBankAccountToLink] = useState(null);
  const [accountFieldErrors, setAccountFieldErrors] = useState({
    name: "",
    number: "",
    type: "",
  });
  // Default values for quick-create modal
  const [accountDefault, setAccountDefault] = useState({});
  const location = useLocation();
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState({});
  const {
    isOpen: isStartOpen,
    onOpen: onStartOpen,
    onClose: onStartClose,
  } = useDisclosure();
  const {
    isOpen: isViewOpen,
    onOpen: onViewOpen,
    onClose: onViewClose,
  } = useDisclosure();
  const context = React.useContext(Context);
  const { profile } = useCurrentProfile();
  const [isMobile] = useMediaQuery("(max-width: 1280px)");
  const [customer, setCustomer] = useState({});
  const [subscriptionSettings, setSubscriptionSettings] = useState({});
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [currentAccount, setCurrentAccount] = useState(null);
  const [isBankAccountModalOpen, setIsBankAccountModalOpen] = useState(false);
  const [bankAccountLoading, setBankAccountLoading] = useState(false);
  const [parentPlaidItem, setParentPlaidItem] = useState(null);

  useEffect(() => {
    let accessToken = AccessToken();
    if (accessToken === null) {
      window.location.href = "/login";
    } else {
      if (customer_id) {
        const getData = async () => {
          var plaidItemsResults = fetchPlaiditems(customer_id);
          var accountResults = fetchCustomerAccounts(
            customer_id,
            { type__name__in: "Bank,Credit Card", is_main_account: false },
            true
          );
          // Fetch raw accounts (for AccountModal parent dropdown)
          var accountRawResults = fetchCustomerAccounts(customer_id);
          // Fetch metadata for AccountModal
          const typeResults = fetchAccountTypes();
          const subTypeResults = fetchAccountSubTypes();
          const taxTypeResults = fetchAccountTaxTypes();
          const subscriptionResults = fetchSubscription(customer_id, {
            status: "active",
          });
          var customerData = fetchCustomer(customer_id);
          var subscriptionSettingsResults = fetchSubscriptionSettings();

          setPlaidItemList(await plaidItemsResults);
          setAccountList(await accountResults);
          setAccountListRaw(await accountRawResults);
          setAccountTypeList(await typeResults);
          setAccountSubTypeList(await subTypeResults);
          setAccountTaxTypeList(await taxTypeResults);

          const subscriptionData = await subscriptionResults;
          if (subscriptionData.length > 0) {
            setSubscription(subscriptionData[0]);
          }
          setCustomer(await customerData);
          setSubscriptionSettings(await subscriptionSettingsResults);
        };
        getData();
      }
      setLoading(false);
    }
  }, [customer_id]);

  useEffect(() => {
    if (location.state?.message) {
      setMessage(location.state.message);
      setTimeout(() => setMessage(""), 3000);
    }
  }, [location.state]);

  const refreshPlaidItemList = (id) => {
    if (id) {
      setLoading(true);

      const getData = async () => {
        var plaidItemsResults = fetchPlaiditems(customer_id);

        setPlaidItemList(await plaidItemsResults);
        setLoading(false);
      };
      getData();
    }
  };

  const addPlaidItemToList = (plaid_item) => {
    setPlaidItemList((prevList) => [...prevList, plaid_item]);
    context.fireToast("Success", "Bank created.", "success");
  };

  const removePlaidItemFromList = (plaid_item_id) => {
    setPlaidItemList((prevList) =>
      prevList.filter((plaid_item) => plaid_item.id !== plaid_item_id)
    );
    context.fireToast("Success", "Bank deleted.", "success");
  };

  const openSubscription = () => {
    if (subscription?.status === "active") {
      onViewOpen();
    } else {
      onStartOpen();
    }
  };

  const openImportModal = (account) => {
    setCurrentAccount(account);
    setImportModalOpen(true);
  };

  const closeImportModal = () => {
    setImportModalOpen(false);
    setCurrentAccount(null);
  };

  const openBankAccountModal = (item_id) => {
    setParentPlaidItem(item_id);
    setIsBankAccountModalOpen(true);
  };
  const closeBankAccountModal = () => {
    setParentPlaidItem(null);
    setIsBankAccountModalOpen(false);
  };

  const handleBankAccountSubmit = async (e, accountData) => {
    e.preventDefault();
    setBankAccountLoading(true);
    accountData.plaiditem = parentPlaidItem;
    await createBankAccount(accountData).then((res) => {
      context.fireToast("Success", "Bank account created.", "success");
      refreshPlaidItemList(customer_id);
    });

    setBankAccountLoading(false);
    closeBankAccountModal();
  };

  const handleImport = async (file) => {
    setImporting(true);
    try {
      if (!file) {
        context.fireToast("Error", "Please select a CSV file.", "error");
        return;
      }
      await importTransactions(currentAccount?.id, file)
        .then((res) => {
          // Success: backend returns string "importing"
          context.fireToast("Success", "Transactions imported", "info");
          closeImportModal();
        })
        .catch((error) => {
          // Error: backend returns 400 with { errors: [...] } where errors is a list of strings
          const errs = error?.response?.data?.errors;
          if (Array.isArray(errs) && errs.length) {
            context.fireToast("Error", errs.join(", "), "error");
          } else {
            context.fireToast(
              "Error",
              error.message || "Import failed",
              "error"
            );
          }
        });
    } finally {
      setImporting(false);
    }
  };

  // Quick-create: open/close and submit handlers
  const openQuickCreate = (bankAccountId, defaultName) => {
    setBankAccountToLink(bankAccountId);
    setAccountFieldErrors({ name: "", number: "", type: "" });
    setAccountDefault({ name: defaultName });
    setIsAccountModalOpen(true);
  };

  const closeQuickCreate = () => {
    setIsAccountModalOpen(false);
    setBankAccountToLink(null);
    setAccountDefault({});
  };

  // Helper to update local state for a bank account's linked accounting account
  const updateLocalBankAccountLink = (
    bankAccountId,
    accountingAccountId,
    accountingAccountObj
  ) => {
    setPlaidItemList((prev) =>
      prev.map((item) => ({
        ...item,
        bankaccount_set: item.bankaccount_set.map((ba) =>
          ba.id === bankAccountId
            ? {
                ...ba,
                accounting_account:
                  accountingAccountObj ||
                  (accountingAccountId ? { id: accountingAccountId } : null),
              }
            : ba
        ),
      }))
    );
  };

  const handleQuickCreateSubmit = async (event, accountData) => {
    event.preventDefault();
    setAccountCreating(true);
    setAccountFieldErrors({ name: "", number: "", type: "" });

    if (accountData.is_main_account) {
      accountData.parent = null;
    }

    try {
      const res = await createAccount(customer_id, false, accountData);

      if (res && res.status < 300) {
        // Optimistically update raw account list with the new account
        setAccountListRaw((prev) => [...prev, res.data]);

        // If the new account matches the linkable filter, append to filtered dropdown options
        const typeLabel = accountTypeList.find(
          (t) => t.value === res.data.type
        )?.label;
        const isLinkable =
          !res.data.is_main_account &&
          (typeLabel === "Bank" || typeLabel === "Credit Card");
        if (isLinkable) {
          const newOption = {
            value: res.data.id,
            label: `${res.data.number} - ${res.data.name}`,
          };
          setAccountList((prev) => [...prev, newOption]);
        }

        // Optionally link the just-created account to the current bank account
        if (bankAccountToLink) {
          await updateBankAccount(bankAccountToLink, {
            accounting_account_id: res.data.id,
          });
          // Update UI state so the dropdown reflects the new link immediately
          updateLocalBankAccountLink(bankAccountToLink, res.data.id, {
            id: res.data.id,
          });
          context.fireToast(
            "Success",
            "Account created and linked.",
            "success"
          );
        } else {
          context.fireToast("Success", "Account created.", "success");
        }
        closeQuickCreate();
      } else if (res && res.data) {
        ["name", "number", "type"].forEach((field) => {
          if (res.data[field]) {
            setAccountFieldErrors((prev) => ({
              ...prev,
              [field]: res.data[field][0],
            }));
          }
        });
        if (!res.data.name && !res.data.number && !res.data.type) {
          context.fireToast(
            "Error",
            res.data.detail || "Unknown error",
            "error"
          );
        }
      } else {
        context.fireToast("Error", "Unknown error", "error");
      }
    } catch (err) {
      console.error("There was an error creating the Account!", err);
      context.fireToast("Error", "Failed to create the account.", "error");
    } finally {
      setAccountCreating(false);
    }
  };

  return (
    <Container
      maxW="100%"
      bgColor="#F4F4F2"
      h={profile?.type !== "client" ? "auto" : "calc(100vh)"}
      p={"0px"}
      overflowY={"auto"}
      position={"relative"}
      minH={"100%"}
    >
      {/* Quick Create Account Modal */}
      <AccountModal
        isOpen={isAccountModalOpen}
        toggle={closeQuickCreate}
        account={accountDefault}
        accountList={accountListRaw}
        accountTypeList={accountTypeList}
        accountSubTypeList={accountSubTypeList}
        accountTaxTypeList={accountTaxTypeList}
        loading={accountCreating}
        handleSubmit={handleQuickCreateSubmit}
        fieldErrors={accountFieldErrors}
      />
      <BankAccountModal
        isOpen={isBankAccountModalOpen}
        onClose={closeBankAccountModal}
        loading={bankAccountLoading}
        handleSubmit={handleBankAccountSubmit}
        accountList={accountListRaw}
      />
      <ImportModal
        isOpen={importModalOpen}
        onClose={closeImportModal}
        loading={importing}
        handleImport={handleImport}
      />
      <Stack
        pt={
          isMobile && profile?.type === "client"
            ? "150px"
            : !isMobile && profile?.type === "client"
            ? "90px"
            : "20px"
        }
        pl={"24px"}
        pr={"24px"}
        width={"100%"}
      >
        <SimpleGrid
          columns={{ base: 1, sm: 1, md: 2 }} // Ensure it switches to 1 column on smaller screens
          pb="24px"
          gap="10px"
          alignItems="center" // Align items properly
        >
          <Text
            fontSize="24px"
            fontWeight={700}
            width="100%"
            whiteSpace="nowrap"
          >
            Plaid Listing
          </Text>

          <Flex
            justifyContent={{ base: "flex-start", md: "flex-end" }}
            overflow="hidden"
          >
            <Box w="100%" maxW="100%">
              <Stack
                direction="row"
                align="center"
                wrap="wrap" // Ensure wrapping instead of overflow
                gap="10px" // Add spacing between buttons
                justify={{ base: "flex-start", md: "flex-end" }} // Align based on screen size
              >
                {isFeatureEnabled(profile, "subscriptions") &&
                  profile?.type !== "client" && (
                    <>
                      {subscription?.status !== "active" && (
                        <Box mr="10px">
                          <CloudOff color="red" />
                        </Box>
                      )}
                      {subscription?.status === "active" && (
                        <Box mr="10px">
                          <Cloud color="green" />
                        </Box>
                      )}
                      <Button onClick={openSubscription}>Subscription</Button>
                    </>
                  )}
                {customer?.name && (
                  <StartSubscriptionModal
                    isOpen={isStartOpen}
                    onClose={onStartClose}
                    setSubscription={setSubscription}
                    plaidItemList={plaidItemList}
                    customer={customer}
                    settings={subscriptionSettings}
                    refreshPlaidItemList={refreshPlaidItemList}
                  />
                )}
                <ViewSubscriptionModal
                  isOpen={isViewOpen}
                  onClose={onViewClose}
                  subscription={subscription}
                  setSubscription={setSubscription}
                  customer_id={customer_id}
                  settings={subscriptionSettings}
                />
                {profile?.type !== "client" && (
                  <PlaidItemCreate
                    customer_id={customer_id}
                    addPlaidItemToList={addPlaidItemToList}
                  />
                )}
                <LinkPlaidButton
                  customer_id={customer_id}
                  refreshList={refreshPlaidItemList}
                  setLoading={setLoading}
                />
                {loading && (
                  <FontAwesomeIcon
                    className="float-end fa-spin"
                    icon={faSpinner}
                    color="green"
                  />
                )}
              </Stack>
            </Box>
          </Flex>
        </SimpleGrid>
        <NotificationContainer rounded={true} p={"12px 0px 0px"} />
        {message && (
          <Alert color="success" className="text-center">
            {message}
          </Alert>
        )}

        {plaidItemList &&
          plaidItemList
            .filter((item) => item.product === "transactions")
            .map((item) => (
              <PlaidItem
                key={item.id}
                item={item}
                customer_id={customer_id}
                accountList={accountList}
                setLoading={setLoading}
                removePlaidItemFromList={removePlaidItemFromList}
                refreshPlaidItemList={refreshPlaidItemList}
                openQuickCreate={openQuickCreate}
                openImportModal={openImportModal}
                onLinked={updateLocalBankAccountLink}
                openBankAccountModal={openBankAccountModal}
              />
            ))}
      </Stack>
    </Container>
  );
};

const PlaidItem = ({
  item,
  customer_id,
  accountList,
  setLoading,
  removePlaidItemFromList,
  refreshPlaidItemList,
  openQuickCreate,
  openImportModal,
  onLinked,
  openBankAccountModal,
}) => {
  const { profile } = useCurrentProfile();

  const hasError = item.plaiditemerror_set.some(
    (error) =>
      error.code === "ITEM_LOGIN_REQUIRED" ||
      error.code === "USER_PERMISSION_REVOKED"
  );

  return (
    <CustomTableContainer p={0} m={"0px 0px 20px"} border={0}>
      <Table>
        <Thead h={"56px"} backgroundColor={"#1A202C"}>
          <Tr key={item.id}>
            <Td>
              <HStack>
                <Text
                  mr={"5px"}
                  backgroundColor={"#1A202C"}
                  fontSize={"20px"}
                  color={"white"}
                  fontWeight={600}
                >
                  {item.name}
                </Text>
                {item.plaiditemerror_set.map((error) => (
                  <Tooltip key={error.id} label={error.message} fontSize="md">
                    <AlertCircle color="red" />
                  </Tooltip>
                ))}
              </HStack>
            </Td>
            <Td width="100px">
              <HStack>
                {profile?.type !== "client" && (
                  <>
                    <Button
                      border={"1px solid #FFFFFF"}
                      colorScheme="blue"
                      backgroundColor={"#1A202C"}
                      gap={1}
                      onClick={() => openBankAccountModal(item.id)}
                    >
                      <Text>Bank account</Text>
                      <AddIcon size={3} />
                    </Button>
                    <ClientPLaidItemDelete
                      customer_id={customer_id}
                      plaiditem_id={item.id}
                      removePlaidItemFromList={removePlaidItemFromList}
                    />
                  </>
                )}
                {hasError && (
                  <LinkPlaidButton
                    customer_id={customer_id}
                    refreshList={refreshPlaidItemList}
                    setLoading={setLoading}
                    plaiditem_id={item.id}
                  />
                )}
              </HStack>
            </Td>
          </Tr>
        </Thead>
      </Table>
      <CustomTableContainer
        p={0}
        border={0}
        borderBottomRadius={0}
        boxShadow={0}
        borderBottom={"1px solid #0000000F"}
        m={"30px 30px 24px"}
      >
        <Table variant="unstyled" size="sm" width="100%">
          <Thead h={"40px"}>
            <Tr>
              <Th width={"72px"}></Th>
              <Th fontSize={"16px"} fontWeight={700}>
                Account Name
              </Th>
              {profile?.type !== "client" && (
                <Th fontSize={"16px"} fontWeight={700}>
                  QB Name
                </Th>
              )}
              {profile?.hasAccountingAccess && profile?.type !== "client" && (
                <Th fontSize={"16px"} fontWeight={700}>
                  Account Link
                </Th>
              )}
              <Th fontSize={"16px"} fontWeight={700}>
                Type
              </Th>
              {profile?.type !== "client" && (
                <>
                  <Th></Th>
                  <Th></Th>
                </>
              )}
              {profile?.type !== "client" ? (
                <Th fontSize={"16px"} fontWeight={700}>
                  Actions
                </Th>
              ) : (
                <Th></Th>
              )}
            </Tr>
          </Thead>
          <Tbody>
            {item.bankaccount_set.map((account) => (
              <BankAccount
                key={account.id}
                account={account}
                accountList={accountList}
                setLoading={setLoading}
                openQuickCreate={openQuickCreate}
                openImportModal={openImportModal}
                onLinked={onLinked}
                parentItemName={item.name}
              />
            ))}
          </Tbody>
          <Tfoot>
            <Tr>
              <Td></Td>
            </Tr>
          </Tfoot>
        </Table>
      </CustomTableContainer>
    </CustomTableContainer>
  );
};

const BankAccount = ({
  account,
  accountList,
  setLoading,
  openQuickCreate,
  openImportModal,
  onLinked,
  parentItemName,
}) => {
  const { profile } = useCurrentProfile();
  const [quickbooksName, setQuickBooksName] = useState(
    account.quickbooks_name ? account.quickbooks_name : "..."
  );
  const [isEditing, setIsEditing] = useState(false);
  const context = React.useContext(Context);
  const onUpdateBankAccount = (selectedAccount) => {
    setLoading(true);

    updateBankAccount(account.id, { accounting_account_id: selectedAccount })
      .then((res) => {
        setLoading(false);
        if (onLinked) onLinked(account.id, selectedAccount);
        context.fireToast("Success", "Account updated", "success");
      })
      .catch((err) => {
        console.error("There was an error updating the bank account!", err);
        context.fireToast(
          "Error",
          "Failed to update the bank account.",
          "error"
        );
      });
  };

  return (
    <Tr key={account.id} h={"52px"}>
      <Td fontSize={"18px"} width={"72px"} textAlign={"center"}>
        {account.plaid_linked ? (
          <LinkIcon color="#536DEC" />
        ) : (
          <LinkIcon color="red" />
        )}
      </Td>
      <Td fontSize={"18px"}>{account.name}</Td>
      {profile?.type !== "client" && (
        <Td>
          <EditQuickbooksName
            bankAccountId={account.id}
            initialName={quickbooksName}
            setQBName={setQuickBooksName}
            isEditing={isEditing}
            setIsEditing={setIsEditing}
          />
        </Td>
      )}
      {profile?.hasAccountingAccess && profile?.type !== "client" && (
        <Td fontSize={"18px"}>
          <HStack spacing={2}>
            <DropDown
              name="account"
              options={accountList}
              selectedValue={account.accounting_account?.id}
              onChange={(account) => onUpdateBankAccount(account.value)}
              w="350px"
              menuPortalTarget={document.body}
            />
            <Tooltip label="Create account" placement="top">
              <IconButton
                aria-label="Create account"
                icon={<AddIcon boxSize={3} />}
                size="sm"
                variant="outline"
                onClick={() =>
                  openQuickCreate(
                    account.id,
                    `${parentItemName}: ${account.name}`
                  )
                }
              />
            </Tooltip>
          </HStack>
        </Td>
      )}
      <Td fontSize={"18px"}>{account.subtype}</Td>
      {profile?.type !== "client" && (
        <>
          <Td></Td>
          <Td></Td>
        </>
      )}
      {profile?.type !== "client" ? (
        <Td>
          <Flex direction={"row"} gap={2}>
            <Button
              backgroundColor={"white"}
              color={"black"}
              _hover={{
                backgroundColor: "#627CFB",
                border: "1px solid #627CFB",
                color: "white",
              }}
              border={"1px solid black"}
              onClick={() => openImportModal(account)}
              cursor="pointer"
              size="sm"
              h="40px"
            >
              <Upload size={15} />
              <Text ml={1}>Import</Text>
            </Button>
            <Button
              backgroundColor={"white"}
              color={"black"}
              _hover={{
                backgroundColor: "#627CFB",
                border: "1px solid #627CFB",
                color: "white",
              }}
              border={"1px solid black"}
              onClick={() => setIsEditing(true)}
              cursor="pointer"
              size="sm"
              h="40px"
            >
              <Edit3 size={15} />
              <Text ml={1}>Edit</Text>
            </Button>
            <PlaidBankTransactionExport account_id={account.id} />
          </Flex>
        </Td>
      ) : (
        <Td></Td>
      )}
    </Tr>
  );
};

const ViewSubscriptionModal = ({
  isOpen,
  onClose,
  subscription,
  setSubscription,
  customer_id,
  settings,
}) => {
  const context = React.useContext(Context);
  const [loading, setLoading] = useState(false);

  const onCancelSubscription = () => {
    setLoading(true);
    cancelSubscription(customer_id, subscription.id)
      .then(() => {
        setSubscription({});
        onClose();
        setLoading(false);
        context.fireToast("Success", "Subscription cancelled.", "success");
      })
      .catch((err) => {
        console.error("There was an error cancelling the subscription!", err);
        context.fireToast(
          "Error",
          "Failed to cancel the subscription.",
          "error"
        );
        setLoading(false);
      });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Subscription</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {subscription?.id && (
            <>
              <Flex justifyContent={"center"}>
                <Text fontWeight={"bold"} fontSize={"24px"}>
                  {renderAmount(subscription.amount)}/{subscription.interval}
                </Text>
              </Flex>
              <Flex justifyContent={"center"}>
                <Text>
                  {titleCase(subscription.status)} since{" "}
                  {subscription.start_date}
                </Text>
              </Flex>
              {subscription?.subscription_type === "stripe" && (
                <Flex justifyContent={"center"}>
                  <Link href={settings.stripe_portal_url} isExternal>
                    Stripe portal <ExternalLinkIcon mx="2px" />
                  </Link>
                </Flex>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            onClick={onClose}
            isDisabled={loading}
            colorScheme="blackAlpha"
          >
            Close
          </Button>
          {subscription?.subscription_type === "plaid" && (
            <Button
              type="button"
              isDisabled={loading}
              ml={3}
              onClick={onCancelSubscription}
            >
              {loading ? <Spinner size="sm" /> : "End Subscription"}
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const StartSubscriptionModal = ({
  isOpen,
  onClose,
  setSubscription,
  customer,
  plaidItemList,
  settings,
  refreshPlaidItemList,
}) => {
  const fetchClientSecret = () => {
    return createCheckoutSession(customer?.id, {}).then(
      (response) => response.data.checkoutSessionClientSecret
    );
  };

  // Only load Stripe if the modal is open and we have a valid public key
  const stripePromise =
    isOpen &&
    settings.stripe_public_key &&
    settings.stripe_public_key.startsWith("pk_")
      ? loadStripe(settings.stripe_public_key)
      : Promise.resolve(null);

  return (
    <CheckoutProvider stripe={stripePromise} options={{ fetchClientSecret }}>
      <StartSubscriptionModalInner
        isOpen={isOpen}
        onClose={onClose}
        setSubscription={setSubscription}
        customer={customer}
        plaidItemList={plaidItemList}
        settings={settings}
        refreshPlaidItemList={refreshPlaidItemList}
      />
    </CheckoutProvider>
  );
};

const StartSubscriptionModalInner = ({
  isOpen,
  onClose,
  setSubscription,
  customer,
  plaidItemList,
  settings,
  refreshPlaidItemList,
}) => {
  const context = React.useContext(Context);
  const checkout = useCheckout();
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState("plaid");

  const [data, setData] = useState({
    day_of_month: 1,
    start_date: new Date().toISOString().split("T")[0],
    legal_name: customer?.name,
    write_amount: settings?.amount || 0,
  });

  const handleSubmitPlaid = (event) => {
    event.preventDefault();
    setLoading(true);
    data.customer = customer?.id;

    addSubscription(customer?.id, data)
      .then((res) => {
        setSubscription(res.data);
        onClose();
        setLoading(false);
        context.fireToast("Success", "Subscription added.", "success");
      })
      .catch((err) => {
        console.error("There was an error creating the subscription!", err);
        context.fireToast(
          "Error",
          "Failed to create the subscription.",
          "error"
        );
        setLoading(false);
      });
  };

  const handleSubmitStripe = async (event) => {
    event.preventDefault();

    // Check if Stripe is properly configured
    if (
      !settings.stripe_public_key ||
      !settings.stripe_public_key.startsWith("pk_")
    ) {
      context.fireToast("Error", "Stripe is not properly configured.", "error");
      return;
    }

    setLoading(true);

    const result = await checkout.confirm();

    if (result.type === "error") {
      console.log(result.error.message);
      context.fireToast("Error", "Failed to create the subscription.", "error");
    } else {
      const getData = async () => {
        const subscriptionResults = fetchSubscription(customer?.id, {
          status: "active",
        });

        const subscriptionData = await subscriptionResults;
        if (subscriptionData.length > 0) {
          setSubscription(subscriptionData[0]);
        }
      };
      getData();
      context.fireToast("Success", "Subscription added.", "success");
    }
    setLoading(false);
    onClose();
    console.log(new Date());
  };

  const bankAccounts = plaidItemList
    ?.filter((item) => item.product === "transfer")
    ?.map((item) =>
      item.bankaccount_set
        .filter((bankAccount) => bankAccount.type === "depository")
        .map((bankAccount) => {
          return {
            value: bankAccount.id,
            label: bankAccount.name,
          };
        })
    )
    .flat();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setData({ ...data, [name]: value });
  };

  const handleDayChange = (_, day) => {
    const name = "day_of_month";
    setData({ ...data, [name]: day });
  };

  const handleSelectBankAccount = (bankaccount) => {
    setData({ ...data, bankaccount: bankaccount.value });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={"xl"}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Subscription</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex pb={5} justifyContent={"center"}>
            <Text fontWeight={"bold"} fontSize={"24px"}>
              {renderAmount(data.write_amount)}/{settings?.interval}
            </Text>
          </Flex>
          <Tabs>
            <TabList>
              <Tab onClick={() => setType("plaid")}>Plaid</Tab>
              <Tab onClick={() => setType("stripe")}>Stripe</Tab>
            </TabList>

            <TabPanels>
              <TabPanel>
                {bankAccounts.length === 0 && (
                  <>
                    <Alert status="info" mb={4}>
                      No transfer-enabled bank accounts found. Link one below.
                    </Alert>
                    <Stack direction="row" align="center">
                      <Spacer />
                      <LinkPlaidButton
                        customer_id={customer?.id}
                        refreshList={refreshPlaidItemList}
                        product={"transfer"}
                      />
                      <Spacer />
                    </Stack>
                  </>
                )}
                {bankAccounts.length > 0 && (
                  <form id="subscribePlaidForm" onSubmit={handleSubmitPlaid}>
                    <FormControl>
                      <FormLabel htmlFor="bankaccount">Bank Account</FormLabel>
                      <DropDown
                        name="bankaccount"
                        options={bankAccounts}
                        selectedValue={data.parent}
                        onChange={handleSelectBankAccount}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel htmlFor="legal_name">Legal Name</FormLabel>
                      <Input
                        type="text"
                        name="legal_name"
                        defaultValue={data.legal_name}
                        onChange={handleInputChange}
                        required
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel htmlFor="day_of_month">Day of Month</FormLabel>

                      <NumberInput
                        name="day_of_month"
                        value={data.day_of_month}
                        onChange={handleDayChange}
                        required
                      >
                        <NumberInputField />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </FormControl>
                    <FormControl>
                      <FormLabel>Start Date</FormLabel>
                      <Input
                        type="date"
                        name="start_date"
                        value={data.start_date}
                        onChange={handleInputChange}
                        required
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel htmlFor="write_amount">
                        Custom Amount
                      </FormLabel>
                      <Input
                        type="number"
                        name="write_amount"
                        defaultValue={data?.write_amount}
                        onChange={handleInputChange}
                        required
                      />
                    </FormControl>
                  </form>
                )}
              </TabPanel>
              <TabPanel>
                <form id="subscribeStripeForm" onSubmit={handleSubmitStripe}>
                  <PaymentElement />
                </form>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>
        <ModalFooter>
          <Button
            onClick={onClose}
            isDisabled={loading}
            colorScheme="blackAlpha"
          >
            Cancel
          </Button>
          {type === "plaid" && bankAccounts.length > 0 && (
            <Button
              type="submit"
              form="subscribePlaidForm"
              isDisabled={loading}
              ml={3}
            >
              {loading ? <Spinner size="sm" /> : "Subscribe"}
            </Button>
          )}
          {type === "stripe" && (
            <Button
              type="submit"
              form="subscribeStripeForm"
              isDisabled={loading}
              ml={3}
            >
              {loading ? <Spinner size="sm" /> : "Subscribe"}
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export const ImportModal = ({ isOpen, onClose, loading, handleImport }) => {
  const [file, setFile] = useState(null);
  const [previewHeaders, setPreviewHeaders] = useState([]);
  const [previewRows, setPreviewRows] = useState([]);

  React.useEffect(() => {
    if (!isOpen) {
      setFile(null);
      setPreviewHeaders([]);
      setPreviewRows([]);
    }
  }, [isOpen]);

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    setFile(f);
    if (f)
      parseFilePreview(f)
        .then((rows) => {
          if (rows.length > 0) {
            setPreviewHeaders(rows[0]);
            setPreviewRows(rows.slice(1));
          } else {
            setPreviewHeaders([]);
            setPreviewRows([]);
          }
        })
        .catch(() => {
          setPreviewHeaders([]);
          setPreviewRows([]);
        });
    else {
      setPreviewHeaders([]);
      setPreviewRows([]);
    }
  };

  const body = (
    <>
      <FormControl isRequired>
        <FormLabel>File</FormLabel>
        <Input
          type="file"
          accept=".csv"
          sx={{
            "::file-selector-button": {
              height: 10,
              padding: 0,
              mr: 4,
              background: "none",
              border: "none",
              fontWeight: "bold",
            },
          }}
          onChange={handleFileChange}
        />
      </FormControl>

      {previewHeaders && previewHeaders.length > 0 && (
        <Box
          mt={4}
          maxH="220px"
          overflowY="auto"
          borderTop="1px solid #E2E8F0"
          pt={3}
        >
          <Text fontWeight="bold" mb={2}>
            Preview (first {previewRows.length} rows)
          </Text>
          <Table size="sm" variant="simple">
            <Thead>
              <Tr>
                {previewHeaders.map((h, idx) => (
                  <Th key={idx} fontSize="12px" whiteSpace="nowrap">
                    {h || `Column ${idx + 1}`}
                  </Th>
                ))}
              </Tr>
            </Thead>
            <Tbody>
              {previewRows.map((r, i) => (
                <Tr key={i}>
                  {previewHeaders.map((_, j) => (
                    <Td key={j} fontSize="12px" whiteSpace="nowrap">
                      {r[j] ?? ""}
                    </Td>
                  ))}
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}
    </>
  );

  const submitButton = (
    <Button onClick={() => handleImport(file)} isDisabled={loading} ml={3}>
      {loading ? "Processing..." : "Import"}
    </Button>
  );

  return (
    <GenericModal
      isOpen={isOpen}
      onClose={onClose}
      title="Import Transactions"
      loading={loading}
      body={body}
      submitButton={submitButton}
      size={"xl"}
    />
  );
};

export const BankAccountModal = ({
  isOpen,
  onClose,
  loading,
  handleSubmit,
}) => {
  const [accountData, setAccountData] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setAccountData({ ...accountData, [name]: value });
  };

  React.useEffect(() => {
    if (!isOpen) {
      setAccountData({});
    }
  }, [isOpen]);

  const handleClose = () => {
    setAccountData({});
    onClose();
  };

  return (
    <GenericModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Bank Account"
      loading={loading}
      body={
        <form
          id="bankAccountForm"
          onSubmit={(e) => handleSubmit(e, accountData)}
        >
          <FormControl isRequired>
            <FormLabel>Account Name</FormLabel>
            <Input
              name="name"
              value={accountData?.name || ""}
              onChange={handleInputChange}
            />
          </FormControl>
          <FormControl isRequired>
            <FormLabel>Account Number</FormLabel>
            <Input
              name="account_id"
              value={accountData?.account_id || ""}
              onChange={handleInputChange}
            />
          </FormControl>
          <FormControl isRequired>
            <FormLabel>Type</FormLabel>
            <Input
              name="type"
              value={accountData?.type || ""}
              onChange={handleInputChange}
            />
          </FormControl>
          <FormControl isRequired>
            <FormLabel>Sub type</FormLabel>
            <Input
              name="subtype"
              value={accountData?.subtype || ""}
              onChange={handleInputChange}
            />
          </FormControl>
        </form>
      }
      submitButton={
        <Button
          type="submit"
          ml={3}
          form="bankAccountForm"
          isDisabled={loading}
        >
          {loading ? <Spinner size="sm" /> : "Save"}
        </Button>
      }
    />
  );
};
